import { Suspense, useEffect } from 'react'
import { Outlet } from 'react-router-dom'
import { Header } from './components/layout/Header'
import { Footer } from './components/layout/Footer'
import { PageLoader } from './components/common/PageLoader'
import { AppErrorBoundary } from './components/common/AppErrorBoundary'
import { RouteErrorBoundary } from './components/common/RouteErrorBoundary'
import { FontOptimizer } from './components/common/FontOptimizer'
import { setupGlobalErrorHandling } from './utils/errorLogger'
import { DynamicPageTitle } from './components/seo/DynamicPageTitle'
import { SEOProvider } from './components/seo/SEOProvider'
import { initializePerformanceMonitoring, initializeCriticalCSSMonitoring, getPerformanceCollector } from './utils/performanceOptimizer'
import { initializeJavaScriptOptimization, DEFAULT_JS_OPTIMIZATION_CONFIG } from './utils/javascriptOptimizer'
import { AdaptiveResourceHints } from './components/common/ResourceHints'
import { LazyLoadingProvider } from './components/common/LazyComponents'
import { CacheMonitor } from './components/performance/CacheMonitor'
import { SkipNavigation, ARIAEnhancements, KeyboardNavigation } from './components/accessibility'
import { useMobileFirstOptimization } from './hooks/useMobileFirstOptimization'
// Initialize i18n configuration
import './i18n/config'

function App() {
  // Initialize mobile-first optimization for 2025 SEO standards
  useMobileFirstOptimization({
    validateTouchTargets: true,
    monitorViewport: true,
    optimizePerformance: true,
    enhanceAccessibility: true,
    enableDebugging: process.env.NODE_ENV === 'development'
  })

  // Setup global error handling and performance monitoring on app initialization
  useEffect(() => {
    setupGlobalErrorHandling()
    
    // Initialize Core Web Vitals monitoring - 2025 standards
    initializePerformanceMonitoring({
      budgets: {
        LCP: 2000,  // 2.0s - 2025 competitive standard for ranking
        CLS: 0.1,   // 0.1 - Stable layout (unchanged - already optimal)
        FCP: 1500,  // 1.5s - Faster initial paint for better UX
        TTFB: 500,  // 500ms - Improved server response target
        INP: 150    // 150ms - More responsive interactions for mobile-first
      },
      analytics: {
        enabled: process.env.NODE_ENV === 'production',
        batchSize: 5,
        flushInterval: 30000, // 30 seconds
        customDimensions: {
          app_version: import.meta.env.VITE_APP_VERSION || '1.0.0',
          deployment_target: import.meta.env.VITE_DEPLOYMENT_TARGET || 'unknown',
          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
          viewport_width: typeof window !== 'undefined' ? window.innerWidth.toString() : '0',
          viewport_height: typeof window !== 'undefined' ? window.innerHeight.toString() : '0'
        }
      }
    })
    
    // Initialize Critical CSS monitoring
    // Wait for DOM to be ready to accurately detect critical CSS
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        initializeCriticalCSSMonitoring()
      })
    } else {
      initializeCriticalCSSMonitoring()
    }
    
    // Initialize JavaScript optimization system
    // Wait for performance collector to be available
    setTimeout(() => {
      const performanceCollector = getPerformanceCollector()
      const jsOptimizationConfig = {
        ...DEFAULT_JS_OPTIMIZATION_CONFIG,
        enableAnalytics: import.meta.env.VITE_DEPLOYMENT_TARGET !== 'development',
        jsTransferBudget: 400, // KB - conservative for better performance
        jsExecutionBudget: 50, // ms - strict for smooth interactions
        intersectionThreshold: 0.1,
        rootMargin: '100px' // Preload slightly before components are visible
      }
      
      const jsOptimizer = initializeJavaScriptOptimization(jsOptimizationConfig, performanceCollector)
      
      // Log initialization in development
      if (process.env.NODE_ENV === 'development') {
        console.log('📦 JavaScript optimization initialized:', {
          config: jsOptimizationConfig,
          optimizer: jsOptimizer ? 'enabled' : 'disabled'
        })
      }
    }, 1000)
    
    // Log environment information for debugging
    if (process.env.NODE_ENV === 'development') {
      console.log('🚀 Multi-provider static site initialized', {
        deployment_target: import.meta.env.VITE_DEPLOYMENT_TARGET,
        environment: process.env.NODE_ENV,
        performance_monitoring: 'enabled',
        javascript_optimization: 'enabled',
        critical_css_monitoring: 'enabled',
        features: {
          route_splitting: true,
          component_splitting: true,
          resource_hints: true,
          lazy_loading: true,
          tree_shaking: true
        }
      })
    }
  }, [])

  return (
    <AppErrorBoundary>
      <FontOptimizer enablePerformanceMonitoring={process.env.NODE_ENV === 'development'}>
        <SEOProvider>
          <AdaptiveResourceHints>
            <LazyLoadingProvider
              config={{
                isLazyLoadingEnabled: true,
                preloadThreshold: 0.1,
                enabledFeatures: {
                  hoverPreload: true,
                  intersectionObserver: true,
                  resourceHints: true
                }
              }}
            >
              {/* Accessibility Components - loaded first for best UX */}
              <SkipNavigation
                targets={{
                  mainContent: 'main-content',
                  navigation: 'main-navigation',
                  footer: 'page-footer'
                }}
                position="top-left"
                variant="default"
                enableShortcuts={true}
              />
              <ARIAEnhancements
                enableLiveRegions={true}
                enableLandmarkEnhancement={true}
                enableFocusManagement={true}
                enableKeyboardNavigation={true}
              />
              <KeyboardNavigation
                enableShortcuts={true}
                enableRovingTabindex={true}
                enableEscapeKey={true}
              />
              
              <DynamicPageTitle />
              <div className="min-h-screen flex flex-col bg-white">
                <Header id="main-navigation" />
                
                <main id="main-content" className="flex-1">
                  <RouteErrorBoundary>
                    <Suspense fallback={<PageLoader />}>
                      {/* vite-react-ssg handles routing, so we use Outlet */}
                      <Outlet />
                    </Suspense>
                  </RouteErrorBoundary>
                </main>
                
                <Footer id="page-footer" />
              </div>
              
              {/* Cache Monitor - only show in development or when explicitly enabled */}
              {(process.env.NODE_ENV === 'development' || 
                import.meta.env.VITE_ENABLE_CACHE_MONITOR === 'true') && (
                <CacheMonitor />
              )}
            </LazyLoadingProvider>
          </AdaptiveResourceHints>
        </SEOProvider>
      </FontOptimizer>
    </AppErrorBoundary>
  )
}

export default App